
/**
 * Module dependencies.
 */

import { Menu } from 'src/core/menu';
import { NavbarCard } from 'src/types/navbar';
import { color, units } from 'src/styles/utils';
import { isInternalRoute } from './nav-element';
import { useTranslate } from 'src/core/utils/translator';
import MenuCard from './menu-card';
import MenuImage from './menu-image';
import NavLink from './nav-link';
import NavbarButton from './navbar-button';
import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import compact from 'lodash/compact';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import styled from 'styled-components';
import uniq from 'lodash/uniq';

/**
 * `Props` type.
 */

type Props = {
  card?: NavbarCard | null,
  menu: Menu
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  background-color: ${color('beige100')};
  display: flex;
  gap: ${units(5)};
  overflow: hidden;
  padding-left: ${units(7)};
`;

/**
 * `Menu` styled component.
 */

const MenuWrapper = styled.div`
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: ${units(10)};
  min-width: 0;
  padding: ${units(5)} 0;
`;

/**
 * `Groups` styled component.
 */

const Groups = styled.div`
  /*
   * A fixed three column grid, not flex: a column that is still loading must
   * not make the others wider and then snap back when it arrives.
   */
  display: grid;
  gap: ${units(6)};
  grid-template-columns: repeat(3, 1fr);
  padding: 0 ${units(1)};
`;

/**
 * `Group` styled component.
 */

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${units(3)};
  min-width: 0;
`;

/**
 * `GroupTitle` styled component.
 */

const GroupTitle = styled.p`
  color: ${color('black')};
  font-size: 16px;
  font-weight: 600;
  line-height: ${units(3)};
  margin: 0;
`;

/**
 * `List` styled component.
 */

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${units(1.5)};
`;

/**
 * `CardWrapper` styled component.
 */

const CardWrapper = styled.div`
  display: flex;
  flex: 1 1 0;
  min-width: 0;

  > * {
    flex: 1 1 0;
  }
`;

/**
 * `MegaMenu` component.
 */

const MegaMenu = ({ card, menu }: Props): ReactElement => {
  const { locale } = useTranslate();
  const { cta, groups, title } = menu;
  const [activeLinkImage, setActiveLinkImage] = useState<string | null>(null);
  const linkImages = useMemo(() => {
    return uniq(compact(flatMap(groups, ({ links }) => map(links, 'image'))));
  }, [groups]);

  const handleLeave = useCallback(() => {
    setActiveLinkImage(null);
  }, []);

  return (
    <Wrapper
      aria-label={title}
      role={'region'}
    >
      <MenuWrapper>
        <Groups
          onBlur={handleLeave}
          onMouseLeave={handleLeave}
        >
          {map(groups, ({ links, title }) => (
            <Group key={title}>
              <GroupTitle>
                {title}
              </GroupTitle>

              <List>
                {map(links, ({ href, image, label }) => (
                  <li
                    key={label}
                    onFocus={() => setActiveLinkImage(image ?? null)}
                    onMouseEnter={() => setActiveLinkImage(image ?? null)}
                  >
                    <NavLink
                      href={href}
                      {...isInternalRoute(href) && { locale }}
                    >
                      {label}
                    </NavLink>
                  </li>
                ))}
              </List>
            </Group>
          ))}
        </Groups>

        {cta && (
          <div>
            <NavbarButton
              colorTheme={'dark'}
              href={cta.href}
              variant={'fill'}
              {...isInternalRoute(cta.href) && { locale }}
            >
              {cta.label}
            </NavbarButton>
          </div>
        )}
      </MenuWrapper>

      <CardWrapper>
        {linkImages.length > 0 ? (
          <MenuImage
            activeImage={activeLinkImage}
            image={card?.media?.fileUrl ?? linkImages[0]}
            images={linkImages}
          />
        ) : card && (
          <MenuCard card={card} />
        )}
      </CardWrapper>
    </Wrapper>
  );
};

/**
 * Export `MegaMenu` component.
 */

export default MegaMenu;
